import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();

    // Also shimmer the order goal message if it exists
    const orderGoalTextComponents = /** @type {NodeListOf<TextComponent>} */ (
      document.querySelectorAll('dkl-order-goal text-component')
    );
    orderGoalTextComponents.forEach((component) => component.shimmer());
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // Also include the product-information section if there's an order goal widget on the PDP
    const pdpOrderGoal = document.querySelector('dkl-order-goal[data-dkl-widget-type="order-goal-pdp"]');
    if (pdpOrderGoal) {
      const productSection = pdpOrderGoal.closest('.shopify-section');
      if (productSection?.id) {
        sectionsToUpdate.add(productSection.id.replace('shopify-section-', ''));
      }
    }

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        // Morph only cart-items-component elements, not the entire section
        // This prevents dialog elements from being affected
        this.#morphCartItemsComponents(parsedResponseText.sections[this.sectionId]);

        // Fetch the latest cart so CartUpdateEvent always carries a cart
        // payload in its `resource` field. This keeps downstream listeners
        // (like Discount Kit widgets) simple and consistent.
        fetch(`${Theme.routes.cart_url}`, fetchConfig('json', { body }))
          .then((response) => {
            if (!response.ok) return null;
            return response.json();
          })
          .then((cart) => {
            this.dispatchEvent(
              new CartUpdateEvent(cart || {}, this.sectionId, {
                itemCount: newCartItemCount,
                source: 'cart-items-component',
                sections: parsedResponseText.sections,
              })
            );
          })
          .catch((error) => {
            console.error('Failed to fetch cart for CartUpdateEvent:', error);
            // Fallback: still dispatch event without full cart payload
            this.dispatchEvent(
              new CartUpdateEvent({}, this.sectionId, {
                itemCount: newCartItemCount,
                source: 'cart-items-component',
                sections: parsedResponseText.sections,
              })
            );
          });

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      this.#morphCartItemsComponents(cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Morphs all cart-items-component elements within a section HTML
   * without morphing parent elements like dialogs.
   * Also updates dialog attributes (class, aria-labelledby) when cart state changes.
   * @param {string} sectionHTML - The section HTML containing cart-items-component elements
   */
  #morphCartItemsComponents(sectionHTML) {
    const fragment = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const newCartItemsComponents = fragment.querySelectorAll('cart-items-component');

    newCartItemsComponents.forEach((newComponent) => {
      if (!(newComponent instanceof HTMLElement)) return;
      const sectionId = newComponent.dataset.sectionId;
      if (!sectionId) return;

      // Find the existing cart-items-component with the same section ID
      const existingComponents = document.querySelectorAll(
        `cart-items-component[data-section-id="${sectionId}"]`
      );

      existingComponents.forEach((existingComponent) => {
        // Morph only the children of cart-items-component, not the component itself
        // and definitely not any parent dialog elements
        if (existingComponent instanceof HTMLElement) {
          morph(existingComponent, newComponent, { childrenOnly: true });

          // Update parent dialog attributes if this cart-items-component is inside a dialog
          const existingDialog = existingComponent.closest('dialog.cart-drawer__dialog');
          if (existingDialog instanceof HTMLDialogElement) {
            const newDialog = newComponent.closest('dialog.cart-drawer__dialog');
            if (newDialog instanceof HTMLDialogElement) {
              this.#updateDialogAttributes(existingDialog, newDialog);
            }
          }
        }
      });
    });
  }

  /**
   * Updates dialog attributes without re-rendering the dialog element itself.
   * This preserves the dialog's open state and backdrop.
   * @param {HTMLDialogElement} existingDialog - The existing dialog element
   * @param {HTMLDialogElement} newDialog - The new dialog element with updated attributes
   */
  #updateDialogAttributes(existingDialog, newDialog) {
    // Update class attribute (handles cart-drawer--empty class)
    const newClass = newDialog.getAttribute('class');
    if (newClass && newClass !== existingDialog.getAttribute('class')) {
      existingDialog.setAttribute('class', newClass);
    }

    // Update aria-labelledby attribute
    const newAriaLabelledBy = newDialog.getAttribute('aria-labelledby');
    if (newAriaLabelledBy && newAriaLabelledBy !== existingDialog.getAttribute('aria-labelledby')) {
      existingDialog.setAttribute('aria-labelledby', newAriaLabelledBy);
    }
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(
        `quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`
      );

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    const cartQuantitySelectors = document.querySelectorAll('cart-quantity-selector-component');
    for (const selector of cartQuantitySelectors) {
      if ('updateButtonStates' in selector && typeof selector.updateButtonStates === 'function') {
        selector.updateButtonStates();
      }
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
